import { Outlet, Link, useLocation, Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import logoSvg from '../assets/logo.svg'
import Spinner from '../components/shared/Spinner'
import SkipLink from '../components/shared/SkipLink'

const sidebarLinks = [
  { path: '/dashboard', label: 'Overview', icon: '📊' },
  { path: '/dashboard/licenses', label: 'Licenses', icon: '🔑' },
  { path: '/dashboard/settings/profile', label: 'Profile', icon: '👤' },
  { path: '/dashboard/settings/security', label: 'Security', icon: '🔒' },
  { path: '/dashboard/settings/billing', label: 'Billing', icon: '💳' },
]

const companyLinks = [
  { path: '/dashboard/company', label: 'Company', icon: '🏢' },
  { path: '/dashboard/company/members', label: 'Members', icon: '👥' },
  { path: '/dashboard/company/credits', label: 'Credits', icon: '🎫' },
  { path: '/dashboard/company/transactions', label: 'Billing History', icon: '📄' },
]

export default function DashboardLayout() {
  const location = useLocation()
  const { user, isLoading, isAdmin, logout } = useAuth()

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="dashboard-loading" role="status" aria-label="Loading dashboard">
        <Spinner size="large" label="Loading..." />
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return (
    <div className="dashboard">
      {/* Skip Link for keyboard users */}
      <SkipLink href="#dashboard-content" />

      {/* Sidebar */}
      <aside className="dashboard-sidebar" aria-label="Dashboard navigation">
        <Link to="/" className="dashboard-logo" aria-label="PlanScan home">
          <img src={logoSvg} alt="PlanScan" />
        </Link>

        <nav className="dashboard-nav" aria-label="Dashboard menu">
          {sidebarLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`dashboard-nav-link ${
                location.pathname === link.path ? 'active' : ''
              }`}
              aria-current={location.pathname === link.path ? 'page' : undefined}
            >
              <span className="dashboard-nav-icon" aria-hidden="true">{link.icon}</span>
              {link.label}
            </Link>
          ))}
        </nav>

        {user.company && (
          <nav className="dashboard-nav" aria-label="Company menu">
            <span className="dashboard-nav-section-label">Company</span>
            {companyLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`dashboard-nav-link ${
                  location.pathname === link.path ? 'active' : ''
                }`}
                aria-current={location.pathname === link.path ? 'page' : undefined}
              >
                <span className="dashboard-nav-icon" aria-hidden="true">{link.icon}</span>
                {link.label}
              </Link>
            ))}
          </nav>
        )}

        {isAdmin && (
          <nav className="dashboard-nav" aria-label="Admin tools">
            <Link
              to="/admin"
              className="dashboard-nav-link"
              aria-label="Admin Center"
            >
              <span className="dashboard-nav-icon" aria-hidden="true">⚙️</span>
              Admin Center
            </Link>
          </nav>
        )}

        <div className="dashboard-user">
          <span className="dashboard-user-email">{user.email}</span>
          <button
            onClick={logout}
            className="dashboard-logout"
            aria-label="Sign out of your account"
          >
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main id="dashboard-content" className="dashboard-main" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  )
}
